import { LabType, OldLabInfo } from "../models/old-labs.model";
import { inferEgfrContrastMultiplierFromSaved } from "./lab-results.util";

export type LabResultRangeKey =
  | "sodium"
  | "potassium"
  | "chloride"
  | "carbonDioxide"
  | "bun"
  | "creatinine"
  | "glucose"
  | "calcium"
  | "hgb"
  | "hct"
  | "anGap";

export interface LabReferenceRange {
  key: LabResultRangeKey;
  label: string;
  unit: string;
  min: number;
  max: number;
}

export interface LabRangeValue {
  min: number;
  max: number;
  unit: string;
  /**
   * Optional panic limit for status dropdown (`suggestLabStatus`).
   * Values at or below this are `Critical low` when set.
   */
  criticalMin?: number;
  /** Values at or above this are `Critical high` when set. */
  criticalMax?: number;
}

export interface LabRangeWithSamples {
  arterial: LabRangeValue;
  venous: LabRangeValue;
}

export type Chem8LabRangeSpec = LabRangeValue | LabRangeWithSamples;

/** Chem 8 / Full Metabolic reference ranges for procedure room lab field tooltips. */
export const CHEM8_LAB_RANGES: Record<LabResultRangeKey, Chem8LabRangeSpec> = {
  sodium: {
    arterial: { min: 138, max: 146, unit: "mEq/L" },
    venous: { min: 138, max: 146, unit: "mEq/L" },
  },
  bun: {
    arterial: { min: 8, max: 26, unit: "mg/dL" },
    venous: { min: 8, max: 26, unit: "mg/dL" },
  },
  potassium: {
    arterial: { min: 3.5, max: 4.9, unit: "mEq/L" },
    venous: { min: 3.5, max: 4.9, unit: "mEq/L" },
  },
  creatinine: {
    arterial: { min: 0.6, max: 1.3, unit: "mg/dL" },
    venous: { min: 0.6, max: 1.3, unit: "mg/dL" },
  },
  chloride: {
    arterial: { min: 98, max: 109, unit: "mEq/L" },
    venous: { min: 98, max: 109, unit: "mEq/L" },
  },
  calcium: {
    arterial: { min: 8.6, max: 10.3, unit: "mg/dL" },
    venous: { min: 8.6, max: 10.3, unit: "mg/dL" },
  },
  hgb: {
    arterial: { min: 12, max: 17, unit: "g/dL" },
    venous: { min: 12, max: 17, unit: "g/dL" },
  },
  carbonDioxide: {
    arterial: { min: 23, max: 27, unit: "mEq/L" },
    venous: { min: 24, max: 29, unit: "mEq/L" },
  },
  glucose: {
    arterial: {
      min: 70,
      max: 105,
      unit: "mg/dL",
      criticalMin: 40,
      criticalMax: 450,
    },
    venous: {
      min: 70,
      max: 105,
      unit: "mg/dL",
      criticalMin: 40,
      criticalMax: 450,
    },
  },
  hct: { min: 41, max: 50, unit: "%" },
  anGap: { min: 4, max: 12, unit: "mEq/L" },
};

/** Full Metabolic shares the same panel fields and ranges as Chem 8. */
export const FULL_METABOLIC_LAB_RANGES = CHEM8_LAB_RANGES;

function isDualSampleChem8Range(
  spec: Chem8LabRangeSpec
): spec is LabRangeWithSamples {
  return "arterial" in spec && "venous" in spec;
}

function formatChem8LabRangeValue(range: LabRangeValue): string {
  return `${range.min}-${range.max} ${range.unit}`;
}

export function getLabPanelRangeTooltipText(
  key: LabResultRangeKey
): string | null {
  const spec = CHEM8_LAB_RANGES[key];
  if (!spec) {
    return null;
  }
  if (isDualSampleChem8Range(spec)) {
    return [
      `Arterial: ${formatChem8LabRangeValue(spec.arterial)}`,
      `Venous: ${formatChem8LabRangeValue(spec.venous)}`,
    ].join("\n");
  }
  return formatChem8LabRangeValue(spec);
}

export function getLabPanelRangeEnvelope(spec: Chem8LabRangeSpec): LabRangeValue {
  if (isDualSampleChem8Range(spec)) {
    return {
      min: Math.min(spec.arterial.min, spec.venous.min),
      max: Math.max(spec.arterial.max, spec.venous.max),
      unit: spec.arterial.unit,
    };
  }
  return spec;
}

/** Merged optional panic limits from arterial/venous or single-sample Chem 8 spec. */
export function getChem8LabCriticalLimits(
  spec: Chem8LabRangeSpec
): Pick<LabRangeValue, "criticalMin" | "criticalMax"> {
  if (isDualSampleChem8Range(spec)) {
    const criticalMins = [spec.arterial.criticalMin, spec.venous.criticalMin].filter(
      (v): v is number => v !== undefined
    );
    const criticalMaxes = [spec.arterial.criticalMax, spec.venous.criticalMax].filter(
      (v): v is number => v !== undefined
    );
    return {
      ...(criticalMins.length
        ? { criticalMin: Math.min(...criticalMins) }
        : {}),
      ...(criticalMaxes.length
        ? { criticalMax: Math.max(...criticalMaxes) }
        : {}),
    };
  }
  return {
    ...(spec.criticalMin !== undefined ? { criticalMin: spec.criticalMin } : {}),
    ...(spec.criticalMax !== undefined ? { criticalMax: spec.criticalMax } : {}),
  };
}

/** Per-analyte normal + panic limits for Chem 8 / Full Metabolic status dropdown. */
export interface Chem8LabStatusRange {
  low: number;
  high: number;
  criticalLow?: number;
  criticalHigh?: number;
}

export function getChem8LabResultStatusRange(
  key: LabResultRangeKey
): Chem8LabStatusRange | null {
  const spec = CHEM8_LAB_RANGES[key];
  if (!spec) {
    return null;
  }
  const envelope = getLabPanelRangeEnvelope(spec);
  const critical = getChem8LabCriticalLimits(spec);
  return {
    low: envelope.min,
    high: envelope.max,
    ...(critical.criticalMin !== undefined
      ? { criticalLow: critical.criticalMin }
      : {}),
    ...(critical.criticalMax !== undefined
      ? { criticalHigh: critical.criticalMax }
      : {}),
  };
}

export function getLabPanelNormalRangeLabel(key: LabResultRangeKey): string {
  const spec = CHEM8_LAB_RANGES[key];
  if (!spec) {
    return "";
  }
  const envelope = getLabPanelRangeEnvelope(spec);
  return `${envelope.min}–${envelope.max} ${envelope.unit}`;
}

export function getLabPanelDisplayUnit(key: LabResultRangeKey): string {
  const spec = CHEM8_LAB_RANGES[key];
  if (!spec) {
    return LAB_REFERENCE_RANGES[key]?.unit ?? "";
  }
  return getLabPanelRangeEnvelope(spec).unit;
}

/** Out-of-range check for Chem 8 / Full Metabolic panel ranges (tile + tooltip forms). */
export function isLabPanelValueOutOfRange(
  key: LabResultRangeKey,
  value: unknown
): boolean {
  const spec = CHEM8_LAB_RANGES[key];
  if (!spec) {
    return false;
  }
  const num = parseLabNumeric(value);
  if (num === null) {
    return false;
  }
  const envelope = getLabPanelRangeEnvelope(spec);
  return num < envelope.min || num > envelope.max;
}

export function labHasOutOfRangePanelResults(lab: OldLabInfo): boolean {
  const keys = panelAnalyteKeysForLabType(lab.type);
  if (!keys.length) {
    return false;
  }
  const r = lab.labResults || {};
  return keys.some((key) => isLabPanelValueOutOfRange(key, r[key]));
}

export const LAB_REFERENCE_RANGES: Record<
  LabResultRangeKey,
  LabReferenceRange
> = {
  sodium: { key: "sodium", label: "Sodium", unit: "mEq/L", min: 138, max: 146 },
  potassium: {
    key: "potassium",
    label: "Potassium",
    unit: "mEq/L",
    min: 3.5,
    max: 5,
  },
  chloride: {
    key: "chloride",
    label: "Chloride",
    unit: "mEq/L",
    min: 95,
    max: 105,
  },
  carbonDioxide: {
    key: "carbonDioxide",
    label: "Carbon Dioxide",
    unit: "mEq/L",
    min: 23,
    max: 29,
  },
  bun: { key: "bun", label: "BUN", unit: "mg/dL", min: 5, max: 20 },
  creatinine: {
    key: "creatinine",
    label: "Creatinine",
    unit: "mg/dL",
    min: 0.6,
    max: 1.2,
  },
  glucose: { key: "glucose", label: "Glucose", unit: "mg/dL", min: 70, max: 100 },
  calcium: { key: "calcium", label: "Calcium", unit: "mg/dL", min: 8.5, max: 10.5 },
  hgb: { key: "hgb", label: "Hgb", unit: "g/dL", min: 12, max: 17 },
  hct: { key: "hct", label: "HCT", unit: "%", min: 38, max: 51 },
  anGap: { key: "anGap", label: "AnGap", unit: "mEq/L", min: 8, max: 16 },
};

/** Only these fields show range validation in forms and tile display. */
export const VALIDATED_LAB_RANGE_KEYS: readonly LabResultRangeKey[] = [
  "creatinine",
  "hgb",
];

/** Core 8 chemistry analytes (procedure room Chem 8 panel). */
export const CHEM8_PANEL_ANALYTE_KEYS: readonly LabResultRangeKey[] = [
  "sodium",
  "potassium",
  "chloride",
  "carbonDioxide",
  "bun",
  "creatinine",
  "glucose",
  "calcium",
];

export const CHEM8_REQUIRED_FIELDS: readonly LabResultRangeKey[] =
  CHEM8_PANEL_ANALYTE_KEYS;

/** Full Metabolic = Chem 8 plus CBC-related analytes on this panel. */
export const FULL_METABOLIC_PANEL_ANALYTE_KEYS: readonly LabResultRangeKey[] = [
  ...CHEM8_PANEL_ANALYTE_KEYS,
  "hgb",
  "hct",
  "anGap",
];

export const FULL_METABOLIC_REQUIRED_FIELDS: readonly LabResultRangeKey[] =
  FULL_METABOLIC_PANEL_ANALYTE_KEYS;

/** Display order for Full Metabolic in orders-style drawer grids. */
export const PANEL_LAB_DRAWER_FIELD_ORDER: readonly LabResultRangeKey[] = [
  "glucose",
  "bun",
  "creatinine",
  "sodium",
  "potassium",
  "chloride",
  "carbonDioxide",
  "calcium",
  "hgb",
  "hct",
  "anGap",
];

const CHEM8_PANEL_KEY_SET = new Set<LabResultRangeKey>(CHEM8_PANEL_ANALYTE_KEYS);

/** Same layout order as Full Metabolic, minus Hgb/HCT/AnGap. */
export const CHEM8_LAB_DRAWER_FIELD_ORDER: readonly LabResultRangeKey[] =
  PANEL_LAB_DRAWER_FIELD_ORDER.filter((key) => CHEM8_PANEL_KEY_SET.has(key));

export function panelAnalyteKeysForLabType(
  labType: string | null | undefined
): readonly LabResultRangeKey[] {
  const type = String(labType ?? "").trim();
  if (type === LabType.Chem8) {
    return CHEM8_PANEL_ANALYTE_KEYS;
  }
  if (type === LabType.FullMetabolic) {
    return FULL_METABOLIC_PANEL_ANALYTE_KEYS;
  }
  return [];
}

export function panelLabDrawerFieldOrderForLabType(
  labType: string | null | undefined
): readonly LabResultRangeKey[] {
  const type = String(labType ?? "").trim();
  if (type === LabType.Chem8) {
    return CHEM8_LAB_DRAWER_FIELD_ORDER;
  }
  if (type === LabType.FullMetabolic) {
    return PANEL_LAB_DRAWER_FIELD_ORDER;
  }
  return [];
}

export interface LabSummaryPart {
  label: string;
  value: string;
  outOfRange: boolean;
  smallText?: boolean;
}

function parseLabNumeric(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed =
    typeof value === "number" ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

export function isLabRangeValidated(key: LabResultRangeKey): boolean {
  return VALIDATED_LAB_RANGE_KEYS.includes(key);
}

export function isLabValueOutOfRange(
  key: LabResultRangeKey,
  value: unknown
): boolean {
  if (!isLabRangeValidated(key)) {
    return false;
  }
  const num = parseLabNumeric(value);
  if (num === null) {
    return false;
  }
  const range = LAB_REFERENCE_RANGES[key];
  return num < range.min || num > range.max;
}

export function getLabReferenceRangeHint(key: LabResultRangeKey): string {
  const range = LAB_REFERENCE_RANGES[key];
  return `${range.min}-${range.max} ${range.unit}`;
}

export function isLabResultValuePresent(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (typeof value === "string" && value.trim() === "") {
    return false;
  }
  return true;
}

export function getMissingRequiredLabFields(
  values: Partial<Record<LabResultRangeKey, unknown>>,
  requiredKeys: readonly LabResultRangeKey[]
): LabResultRangeKey[] {
  return requiredKeys.filter((key) => !isLabResultValuePresent(values?.[key]));
}

export function areRequiredLabFieldsComplete(
  values: Partial<Record<LabResultRangeKey, unknown>>,
  requiredKeys: readonly LabResultRangeKey[]
): boolean {
  return getMissingRequiredLabFields(values, requiredKeys).length === 0;
}

interface LabSummaryFieldConfig {
  rangeKey: LabResultRangeKey;
  resultKey: keyof OldLabInfo["labResults"];
  displayLabel: string;
}

const FULL_METABOLIC_SUMMARY_FIELDS: readonly LabSummaryFieldConfig[] = [
  { rangeKey: "glucose", resultKey: "glucose", displayLabel: "Glucose" },
  { rangeKey: "sodium", resultKey: "sodium", displayLabel: "Na" },
  { rangeKey: "potassium", resultKey: "potassium", displayLabel: "K" },
  { rangeKey: "chloride", resultKey: "chloride", displayLabel: "Cl" },
  { rangeKey: "bun", resultKey: "bun", displayLabel: "BUN" },
  { rangeKey: "creatinine", resultKey: "creatinine", displayLabel: "Cr" },
  { rangeKey: "calcium", resultKey: "calcium", displayLabel: "Calcium" },
  { rangeKey: "carbonDioxide", resultKey: "carbonDioxide", displayLabel: "CO2" },
  { rangeKey: "anGap", resultKey: "anGap", displayLabel: "AnGap" },
  { rangeKey: "hgb", resultKey: "hgb", displayLabel: "Hgb" },
  { rangeKey: "hct", resultKey: "hct", displayLabel: "HCT" },
];

const CHEM8_SUMMARY_FIELDS: readonly LabSummaryFieldConfig[] =
  CHEM8_PANEL_ANALYTE_KEYS.map((rangeKey) => {
    const field = FULL_METABOLIC_SUMMARY_FIELDS.find((f) => f.rangeKey === rangeKey);
    return (
      field ?? {
        rangeKey,
        resultKey: rangeKey as keyof OldLabInfo["labResults"],
        displayLabel: rangeKey,
      }
    );
  });

/** Chem 8 tile: 3 columns × 3 rows (8 analytes). */
const CHEM8_TILE_GRID_ROWS: readonly (readonly (LabResultRangeKey | null)[])[] = [
  ["sodium", "potassium", "chloride"],
  ["carbonDioxide", "bun", "creatinine"],
  ["glucose", "calcium", null],
];

/** Full Metabolic tile: adds Hgb, HCT, AnGap. */
const FULL_METABOLIC_TILE_GRID_ROWS: readonly (readonly (LabResultRangeKey | null)[])[] =
  [
    ["sodium", "potassium", "chloride"],
    ["carbonDioxide", "bun", "creatinine"],
    ["glucose", "calcium", "hct"],
    ["hgb", "anGap", null],
  ];

function labTileGridRowsForType(
  labType: string
): readonly (readonly (LabResultRangeKey | null)[])[] {
  if (labType === LabType.Chem8) {
    return CHEM8_TILE_GRID_ROWS;
  }
  if (labType === LabType.FullMetabolic) {
    return FULL_METABOLIC_TILE_GRID_ROWS;
  }
  return [];
}

const LAB_SUMMARY_FIELD_BY_KEY = new Map<LabResultRangeKey, LabSummaryFieldConfig>(
  [...FULL_METABOLIC_SUMMARY_FIELDS, ...CHEM8_SUMMARY_FIELDS].map((field) => [
    field.rangeKey,
    field,
  ])
);

function buildPartsFromFields(
  results: OldLabInfo["labResults"] | undefined,
  fields: readonly LabSummaryFieldConfig[]
): LabSummaryPart[] {
  const r = results || {};
  const parts: LabSummaryPart[] = [];

  for (const field of fields) {
    let value = r[field.resultKey];
    if (
      field.resultKey === "hgb" &&
      (value === "" || value == null) &&
      r.hemoglobin !== "" &&
      r.hemoglobin != null
    ) {
      value = r.hemoglobin;
    }
    if (value === "" || value == null) {
      continue;
    }
    const displayValue = String(value);
    parts.push({
      label: field.displayLabel,
      value: displayValue,
      outOfRange: isLabValueOutOfRange(field.rangeKey, value),
    });
  }

  return parts;
}

export function buildLabSummaryParts(lab: OldLabInfo): LabSummaryPart[] {
  const r = lab.labResults || {};
  switch (lab.type) {
    case LabType.FullMetabolic:
      return buildPartsFromFields(r, FULL_METABOLIC_SUMMARY_FIELDS);
    case LabType.Chem8:
      return buildPartsFromFields(r, CHEM8_SUMMARY_FIELDS);
    case LabType.PT:
      return buildPtSummaryParts(r);
    case LabType.INR:
      return buildInrSummaryParts(r);
    default:
      return [];
  }
}

function buildSummaryPartForField(
  results: OldLabInfo["labResults"] | undefined,
  field: LabSummaryFieldConfig
): LabSummaryPart | null {
  const r = results || {};
  let value = r[field.resultKey];
  if (
    field.resultKey === "hgb" &&
    (value === "" || value == null) &&
    r.hemoglobin !== "" &&
    r.hemoglobin != null
  ) {
    value = r.hemoglobin;
  }
  if (value === "" || value == null) {
    return null;
  }
  return {
    label: field.displayLabel,
    value: String(value),
    outOfRange: isLabValueOutOfRange(field.rangeKey, value),
  };
}

/** 3 columns × 4 rows (11 analytes) in a stable layout for procedure-room lab tiles. */
export function buildLabSummaryGrid(
  lab: OldLabInfo
): (LabSummaryPart | null)[][] | null {
  if (lab.type !== LabType.Chem8 && lab.type !== LabType.FullMetabolic) {
    return null;
  }
  const r = lab.labResults || {};
  const grid = labTileGridRowsForType(lab.type).map((row) =>
    row.map((key) => {
      if (!key) {
        return null;
      }
      const field = LAB_SUMMARY_FIELD_BY_KEY.get(key);
      return field ? buildSummaryPartForField(r, field) : null;
    })
  );
  const hasAny = grid.some((row) => row.some((cell) => cell != null));
  return hasAny ? grid : null;
}

export const LAB_TILE_EMPTY_VALUE = "—";

function buildSummaryPartForFieldOrPlaceholder(
  results: OldLabInfo["labResults"] | undefined,
  field: LabSummaryFieldConfig
): LabSummaryPart {
  const part = buildSummaryPartForField(results, field);
  if (part) {
    return part;
  }
  return {
    label: field.displayLabel,
    value: LAB_TILE_EMPTY_VALUE,
    outOfRange: false,
  };
}

/** Procedure-room tile columns: each grid row → one vertical column (Na/K/Cl, CO2/BUN/Cr, …). */
export function buildLabPanelTileResultColumns(
  lab: OldLabInfo
): LabSummaryPart[][] {
  if (lab.type !== LabType.FullMetabolic && lab.type !== LabType.Chem8) {
    return [];
  }
  const r = lab.labResults || {};
  return labTileGridRowsForType(lab.type).map((rowKeys) =>
    rowKeys
      .map((key) => {
        if (!key) {
          return null;
        }
        const field = LAB_SUMMARY_FIELD_BY_KEY.get(key);
        return field ? buildSummaryPartForFieldOrPlaceholder(r, field) : null;
      })
      .filter((part): part is LabSummaryPart => part != null)
  ).filter((column) => column.length > 0);
}

export function isLabTilePlaceholderValue(value: string | undefined): boolean {
  return String(value ?? "").trim() === LAB_TILE_EMPTY_VALUE;
}

export function formatLabSummaryParts(parts: LabSummaryPart[]): string {
  if (!parts.length) {
    return "Results recorded";
  }
  return parts.map((p) => `${p.label} ${p.value}`).join(" · ");
}

export function summarizeLabTile(lab: OldLabInfo): string {
  const parts = buildLabSummaryParts(lab);
  if (parts.length) {
    return formatLabSummaryParts(parts);
  }

  const r = lab.labResults || {};
  switch (lab.type) {
    case LabType.PregnancyTest: {
      const results = r as OldLabInfo["labResults"] & {
        pregnancyTestResult?: string;
      };
      const result = String(
        results.pregnancyTest || results.pregnancyTestResult || lab.notes || ""
      ).trim();
      return formatPregnancyTestSummary(result) || "Result recorded";
    }
    case LabType.EGFR:
      return summarizeEgfrResults(r);
    case LabType.PT: {
      const parts = buildPtSummaryParts(r);
      return parts.length ? formatLabSummaryParts(parts) : "Recorded";
    }
    case LabType.INR: {
      const parts = buildInrSummaryParts(r);
      return parts.length ? formatLabSummaryParts(parts) : "Recorded";
    }
    default:
      return lab.labDate ? `Lab date ${lab.labDate}` : "Recorded";
  }
}

function formatPregnancyTestSummary(value: string): string {
  switch (value.trim().toLowerCase()) {
    case "positive":
      return "Positive";
    case "negative":
      return "Negative";
    case "n/a":
    case "na":
      return "N/A";
    default:
      return value.trim();
  }
}

export type PregnancyTestStatusVariant = "positive" | "negative" | "neutral";

export interface PregnancyTestStatusBadge {
  label: string;
  variant: PregnancyTestStatusVariant;
}

export function getPregnancyTestStatusBadge(
  lab: OldLabInfo
): PregnancyTestStatusBadge | null {
  const results = (lab.labResults || {}) as OldLabInfo["labResults"] & {
    pregnancyTestResult?: string;
  };
  const raw = String(
    results.pregnancyTest || results.pregnancyTestResult || lab.notes || ""
  ).trim();
  if (!raw) {
    return null;
  }
  const label = formatPregnancyTestSummary(raw);
  if (!label) {
    return null;
  }
  switch (raw.toLowerCase()) {
    case "positive":
      return { label, variant: "positive" };
    case "negative":
      return { label, variant: "negative" };
    default:
      return { label, variant: "neutral" };
  }
}

export function buildEgfrSummaryParts(
  results: OldLabInfo["labResults"] | undefined
): LabSummaryPart[] {
  const r = results || {};
  const parts: LabSummaryPart[] = [];
  if (r.eGFR !== "" && r.eGFR != null) {
    parts.push({
      label: "eGFR:",
      value: String(r.eGFR),
      outOfRange: false,
    });
  }
  if (r.maxContrast !== "" && r.maxContrast != null) {
    parts.push({
      label: "Max Contrast:",
      value: String(r.maxContrast),
      outOfRange: false,
    });
    if (r.eGFR !== "" && r.eGFR != null) {
      parts.push({
        label: "eGFR x",
        value: String(
          inferEgfrContrastMultiplierFromSaved(r.eGFR, r.maxContrast)
        ),
        outOfRange: false,
        smallText: true,
      });
    }
  }
  return parts;
}

export const PT_INR_MAX_VALUE = 1.7;

export function getPtInrRangeHint(): string {
  return `≤ ${PT_INR_MAX_VALUE}`;
}

export function isPtInrValueOutOfRange(
  value: number | "" | null | undefined
): boolean {
  if (value === "" || value == null) {
    return false;
  }
  const num = Number(value);
  return !Number.isNaN(num) && num > PT_INR_MAX_VALUE;
}

export function labHasOutOfRangePtInrResult(lab: OldLabInfo): boolean {
  if (lab.type === LabType.PT) {
    return isPtInrValueOutOfRange(lab.labResults?.ptResult);
  }
  if (lab.type === LabType.INR) {
    return isPtInrValueOutOfRange(lab.labResults?.inrResult);
  }
  return false;
}

export function buildPtSummaryParts(
  results: OldLabInfo["labResults"] | undefined
): LabSummaryPart[] {
  const r = results || {};
  const parts: LabSummaryPart[] = [];
  if (r.ptResult !== "" && r.ptResult != null) {
    parts.push({
      label: "PT:",
      value: String(r.ptResult),
      outOfRange: isPtInrValueOutOfRange(r.ptResult),
    });
  }
  return parts;
}

export function buildInrSummaryParts(
  results: OldLabInfo["labResults"] | undefined
): LabSummaryPart[] {
  const r = results || {};
  const parts: LabSummaryPart[] = [];
  if (r.inrResult !== "" && r.inrResult != null) {
    parts.push({
      label: "INR:",
      value: String(r.inrResult),
      outOfRange: isPtInrValueOutOfRange(r.inrResult),
    });
  }
  return parts;
}

export function summarizeEgfrResults(
  results: OldLabInfo["labResults"] | undefined
): string {
  const r = results || {};
  const parts: string[] = [];
  if (r.eGFR !== "" && r.eGFR != null) {
    parts.push(`eGFR: ${r.eGFR}`);
  }
  if (r.maxContrast !== "" && r.maxContrast != null) {
    parts.push(`Max Contrast: ${r.maxContrast}`);
    if (r.eGFR !== "" && r.eGFR != null) {
      parts.push(
        `eGFR x ${inferEgfrContrastMultiplierFromSaved(r.eGFR, r.maxContrast)}`
      );
    }
  }
  return parts.length ? parts.join(" · ") : "Results recorded";
}
